import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Separator } from "../ui/separator";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Activity, Settings, Link2, Eye } from "lucide-react";
import { zktecoAPI } from "../../services/api";

const toLocalFromUtcString = (value) => {
  if (!value) return null;
  try {
    const normalized = typeof value === "string" ? value.replace(" ", "T") : value;
    const date = new Date(
      normalized && typeof normalized === "string" && !normalized.endsWith("Z") ? `${normalized}Z` : normalized
    );
    return date;
  } catch {
    return null;
  }
};

const TerminalDetailsModal = ({
  open,
  onOpenChange,
  terminal,
  diag,
  onEdit,
  onRefresh,
}) => {
  const { t } = useTranslation();
  if (!terminal) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold flex items-center gap-2">
            <Eye className="h-5 w-5" />
            {t("terminalMgmt.details.title", { name: terminal.name })}
          </DialogTitle>
          <DialogDescription>{t("terminalMgmt.details.subtitle")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className={`inline-block w-2.5 h-2.5 rounded-full ${terminal.status === "online" ? "bg-emerald-500" : "bg-gray-400"}`} />
                <div>
                  <div className="text-sm text-muted-foreground">{t("terminalMgmt.details.currentStatus")}</div>
                  <div className="font-medium">{terminal.status === "online" ? t("terminalMgmt.details.online") : t("terminalMgmt.details.offline")}</div>
                </div>
              </div>
              <div className="text-right text-xs">
                <div className="text-muted-foreground">{t("terminalMgmt.details.lastSync")}</div>
                <div className="font-medium">
                  {terminal.last_sync_at ? toLocalFromUtcString(terminal.last_sync_at)?.toLocaleString() : t("terminalMgmt.details.never")}
                </div>
                <div className="mt-1 text-muted-foreground">{t("terminalMgmt.details.lastContact")}</div>
                <div className="font-medium">
                  {terminal.last_ping_at ? toLocalFromUtcString(terminal.last_ping_at)?.toLocaleString() : "—"}
                </div>
              </div>
            </div>
            <Separator className="my-3" />
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    await zktecoAPI.health();
                    toast.success(t("terminalMgmt.details.healthOk"));
                  } catch {}
                }}
              >
                <Activity className="w-4 h-4 mr-2" /> {t("terminalMgmt.details.testHealth")}
              </Button>
              <Button variant="secondary" onClick={() => navigator.clipboard.writeText(terminal.serial_number || "")}>
                📋 {t("terminalMgmt.details.copySn")}
              </Button>
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    await zktecoAPI.refreshTerminalMetrics(terminal.id);
                    toast.success(t("terminalMgmt.details.refreshSent"));
                    setTimeout(() => onRefresh(terminal), 2000);
                  } catch {
                    toast.error(t("terminalMgmt.details.refreshFailed"));
                  }
                }}
              >
                ↻ {t("terminalMgmt.details.refreshMetrics")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const url = `${window.location.protocol}//${window.location.hostname}/api/v1/zkteco/push`;
                  navigator.clipboard.writeText(url);
                  toast.success(t("terminalMgmt.details.admsCopied"));
                }}
              >
                <Link2 className="w-4 h-4 mr-2" /> {t("terminalMgmt.details.copyAdmsUrl")}
              </Button>
            </div>
          </Card>

          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="border-b pb-2">
                <h3 className="text-lg font-medium text-foreground">{t("terminalMgmt.details.identification")}</h3>
              </div>
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-medium text-foreground">{t("terminalMgmt.details.name")}</Label>
                  <p className="text-foreground font-medium">{terminal.name}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-foreground">{t("terminalMgmt.details.serialNumber")}</Label>
                  <p className="text-foreground font-mono text-sm">{terminal.serial_number || t("terminalMgmt.details.notDefined")}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-foreground">{t("terminalMgmt.details.model")}</Label>
                  <p className="text-foreground">
                    {diag?.device_info?.name || terminal.model || (terminal.communication_mode === "push" ? "ZKTeco ADMS Device" : "ZKTeco Terminal")}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-foreground">{t("terminalMgmt.details.firmware")}</Label>
                  <p className="text-foreground">{diag?.device_info?.firmware || terminal.firmware_version || "—"}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-foreground">{t("terminalMgmt.details.pushService")}</Label>
                  <p className="text-foreground">{diag?.device_info?.platform || "—"}</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="border-b pb-2">
                <h3 className="text-lg font-medium text-foreground">{t("terminalMgmt.details.configuration")}</h3>
              </div>
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-medium text-foreground">{t("terminalMgmt.details.commMode")}</Label>
                  <Badge variant={terminal.communication_mode === "push" ? "default" : "secondary"}>
                    {terminal.communication_mode === "push" ? t("terminalMgmt.details.pushAdms") : t("terminalMgmt.details.pullLocal")}
                  </Badge>
                </div>
                <div>
                  <Label className="text-sm font-medium text-foreground">{t("terminalMgmt.details.terminalType")}</Label>
                  <Badge variant="outline">{terminal.terminal_type}</Badge>
                </div>
                {terminal.ip_address && (
                  <div>
                    <Label className="text-sm font-medium text-foreground">{t("terminalMgmt.details.networkAddr")}</Label>
                    <p className="text-foreground font-mono text-sm">{terminal.ip_address}:{terminal.port}</p>
                  </div>
                )}
                <div>
                  <Label className="text-sm font-medium text-foreground">{t("terminalMgmt.details.site")}</Label>
                  <p className="text-foreground">📍 {terminal.site_name || t("terminalMgmt.details.siteNotDefined")}</p>
                </div>
              </div>
            </div>
          </div>

          {terminal.capabilities && terminal.capabilities.length > 0 && (
            <div className="space-y-4">
              <div className="border-b pb-2">
                <h3 className="text-lg font-medium text-foreground">{t("terminalMgmt.details.capabilities")}</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {terminal.capabilities.map((cap) => (
                  <Badge key={cap} variant="outline" className="flex items-center gap-1">
                    {cap === "fingerprint" && "👆"}
                    {cap === "face" && "👤"}
                    {cap === "rfid" && "💳"}
                    {cap === "password" && "🔢"}
                    {cap}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-4">
            <Card className="p-4 text-center">
              <div className="text-sm text-muted-foreground">{t("terminalMgmt.details.users")}</div>
              <div className="text-2xl font-bold text-blue-600">
                {(diag?.device_info?.users_count ?? 0).toLocaleString()} /{" "}
                {(diag?.device_info?.users_max || terminal.max_users || 1000).toLocaleString()}
              </div>
            </Card>
            <Card className="p-4 text-center">
              <div className="text-sm text-muted-foreground">{t("terminalMgmt.details.attRecords")}</div>
              <div className="text-2xl font-bold text-green-600">
                {(diag?.device_info?.attendance_count ?? 0).toLocaleString()} /{" "}
                {(diag?.device_info?.attendance_max || terminal.max_logs || 50000).toLocaleString()}
              </div>
            </Card>
            <Card className="p-4 text-center">
              <div className={`text-2xl font-bold ${terminal.is_active ? "text-green-600" : "text-red-600"}`}>
                {terminal.is_active ? t("terminalMgmt.details.active") : t("terminalMgmt.details.inactive")}
              </div>
              <div className="text-sm text-muted-foreground">{t("terminalMgmt.details.state")}</div>
            </Card>
          </div>

          <div className="mt-4">
            <div className="border-b pb-2 mb-3">
              <h3 className="text-lg font-medium text-foreground">{t("terminalMgmt.details.terminalCapabilities")}</h3>
            </div>
            <div className="flex flex-wrap gap-2 text-sm">
              <Badge variant="outline">{t("terminalMgmt.details.adminUser")}: {diag?.device_info?.admin_count ?? "—"}</Badge>
              <Badge variant="outline">{t("terminalMgmt.details.password")}: {diag?.device_info?.password_count ?? "—"}</Badge>
              <Badge variant="outline">{t("terminalMgmt.details.face")}: {diag?.device_info?.face_count ?? "—"}</Badge>
              <Badge variant="outline">{t("terminalMgmt.details.palm")}: {diag?.device_info?.palm_count ?? "—"}</Badge>
              <Badge variant="outline">{t("terminalMgmt.details.fingerprint")}: {diag?.device_info?.finger_count ?? "—"}</Badge>
              <Badge variant="outline">{t("terminalMgmt.details.card")}: {diag?.device_info?.card_count ?? "—"}</Badge>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end pt-6 border-t space-x-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="px-6">
            {t("terminalMgmt.details.close")}
          </Button>
          <Button
            onClick={() => { onOpenChange(false); onEdit(terminal); }}
            className="px-6 bg-blue-600 hover:bg-blue-700"
          >
            <Settings className="w-4 h-4 mr-2" />
            {t("terminalMgmt.details.edit")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TerminalDetailsModal;
